import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Badge, Box } from "@mui/material";
import { NotificationsOutlined } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import NotificationPanel from "../notifications/NotificationPanel.jsx";
import utils from "../../universal/utils";
import { MOBILE_NAV_MEDIA } from "./layoutConstants";

const basePath = utils.getEdgeBasePath();
const { fetchJsonWithDiagnostics } = utils;

const MOBILE_MQ = `@media ${MOBILE_NAV_MEDIA}`;

export const TOPBAR_ACTION_ICON_SX = {
    fontSize: { xs: "1.25rem", xl: "1.4rem" },
    [MOBILE_MQ]: {
        fontSize: "1.05rem",
    },
};

export const TOPBAR_ACTION_SX = {
    position: "relative",
    width: { xs: "2.6rem", xl: "2.8rem" },
    height: { xs: "2.6rem", xl: "2.8rem" },
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    background: `
        linear-gradient(
            180deg,
            rgba(255,255,255,0.05) 0%,
            rgba(255,255,255,0.025) 100%
        )
    `,
    border: `1px solid rgba(255,255,255,0.05)`,
    color: colors.text.secondary,
    backdropFilter: "blur(12px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04),
        0 6px 18px rgba(0,0,0,0.35)
    `,
    transition: "all 240ms cubic-bezier(0.22,1,0.36,1)",
    cursor: "pointer",
    [MOBILE_MQ]: {
        width: "2.15rem",
        height: "2.15rem",
        borderRadius: "11px",
        "&:hover": {
            transform: "none",
        },
    },
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: `
            linear-gradient(
                115deg,
                transparent 30%,
                rgba(255,255,255,0.08) 50%,
                transparent 70%
            )
        `,
        transform: "translateX(-140%)",
        transition: "transform 900ms cubic-bezier(0.22,1,0.36,1)",
    },
    "&:hover": {
        transform: "translateY(-2px) scale(1.03)",
        borderColor: "rgba(232,163,61,0.16)",
        color: colors.primary.main,
        boxShadow: `
            0 10px 28px rgba(0,0,0,0.45),
            0 0 24px rgba(232,163,61,0.12),
            inset 0 1px 0 rgba(255,255,255,0.06)
        `,
        "&::before": {
            transform: "translateX(140%)",
        },
    },
    "&:active": {
        transform: "scale(0.96)",
    },
};

const TOPBAR_ACTION_BADGE_SX = {
    display: "inline-flex",
    flexShrink: 0,
    overflow: "visible",
    "& .MuiBadge-badge": {
        background: colors.gradient.accent,
        color: colors.text.contrast,
        minWidth: { xs: "1.1rem", xl: "1.35rem" },
        height: { xs: "1.1rem", xl: "1.35rem" },
        borderRadius: "999px",
        fontSize: { xs: "0.58rem", xl: "0.68rem" },
        fontWeight: 600,
        border: `2px solid ${colors.background.base}`,
        boxShadow: `0 0 12px rgba(232,163,61,0.35)`,
        zIndex: 1,
        top: { xs: 6, xl: 7 },
        right: { xs: 6, xl: 7 },
        [MOBILE_MQ]: {
            minWidth: "0.95rem",
            height: "0.95rem",
            fontSize: "0.52rem",
            borderWidth: "1.5px",
            top: 4,
            right: 4,
        },
    },
};

const TopBarActionButton = forwardRef(function TopBarActionButton(
    {
        badgeContent,
        badgeTone = "accent",
        onClick,
        children,
        "aria-label": ariaLabel,
        "aria-expanded": ariaExpanded,
        "aria-haspopup": ariaHaspopup,
        ...rest
    },
    ref,
) {
    const actionButton = (
        <Box
            ref={ref}
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            aria-expanded={ariaExpanded}
            aria-haspopup={ariaHaspopup}
            onClick={onClick}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onClick?.(event);
                }
            }}
            {...rest}
            sx={{
                ...TOPBAR_ACTION_SX,
                ...(badgeTone === "info"
                    ? {
                        "&:hover": {
                            ...TOPBAR_ACTION_SX["&:hover"],
                            color: "info.main",
                        },
                    }
                    : {}),
                ...(rest.sx || {}),
            }}
        >
            {children}
        </Box>
    );

    const showBadge =
        badgeContent !== undefined &&
        badgeContent !== null &&
        Number(badgeContent) > 0;

    if (!showBadge) {
        return actionButton;
    }

    return (
        <Badge
            badgeContent={badgeContent}
            overlap="rectangular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
                ...TOPBAR_ACTION_BADGE_SX,
                "& .MuiBadge-badge": {
                    ...TOPBAR_ACTION_BADGE_SX["& .MuiBadge-badge"],
                    ...(badgeTone === "info"
                        ? {
                            backgroundColor: "info.main",
                            backgroundImage: "none",
                            boxShadow: "none",
                        }
                        : { background: colors.gradient.accent }),
                },
            }}
        >
            {actionButton}
        </Badge>
    );
});

TopBarActionButton.propTypes = {
    badgeContent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    badgeTone: PropTypes.oneOf(["accent", "info"]),
    onClick: PropTypes.func,
    children: PropTypes.node,
    "aria-label": PropTypes.string,
    "aria-expanded": PropTypes.bool,
    "aria-haspopup": PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export function TopBarNotificationAction({
    mongoUserId = null,
    userId = null,
    routePathname = "/",
}) {
    const anchorRef = useRef(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const unreadCountDebugContext = useMemo(
        () => ({
            route: routePathname,
            userId: userId || null,
            mongoUserId: mongoUserId || null,
        }),
        [routePathname, userId, mongoUserId],
    );

    const fetchUnreadCount = useCallback(async () => {
        if (!mongoUserId) {
            return;
        }

        const result = await fetchJsonWithDiagnostics(
            `${basePath}/api/v1/notifications/unread-count`,
            { credentials: "include" },
            {
                context: "TopBar unread notification count",
                extra: unreadCountDebugContext,
            },
        );

        if (result.ok && typeof result.data?.count === "number") {
            setUnreadCount(result.data.count);
        }
    }, [mongoUserId, unreadCountDebugContext]);

    useEffect(() => {
        if (!mongoUserId) {
            return undefined;
        }

        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);

        return () => clearInterval(interval);
    }, [fetchUnreadCount, mongoUserId]);

    const handleTogglePanel = () => {
        setPanelOpen((prev) => !prev);
    };

    const handleClosePanel = () => {
        setPanelOpen(false);
        fetchUnreadCount();
    };

    if (!mongoUserId) {
        return null;
    }

    return (
        <>
            <Box ref={anchorRef} sx={{ display: "inline-flex", flexShrink: 0 }}>
                <TopBarActionButton
                    aria-label="Notifications"
                    aria-haspopup="true"
                    aria-expanded={panelOpen}
                    badgeContent={unreadCount}
                    onClick={handleTogglePanel}
                >
                    <NotificationsOutlined sx={TOPBAR_ACTION_ICON_SX} />
                </TopBarActionButton>
            </Box>

            <NotificationPanel
                open={panelOpen}
                anchorEl={anchorRef.current}
                onClose={handleClosePanel}
                placeholderCount={unreadCount}
            />
        </>
    );
}

TopBarNotificationAction.propTypes = {
    mongoUserId: PropTypes.string,
    userId: PropTypes.string,
    routePathname: PropTypes.string,
};

export default TopBarActionButton;
